// Firebase v10 Client - Dynamic ESM loader from Google CDN with Cloudflare Worker Config Shield
// Worker: https://async.berrymodapk.com

export const WORKER_URL = "https://async.berrymodapk.com";

// Bersihkan seluruh jejak konfigurasi plaintext dari browser storage (Zero Storage Footprint)
try {
  sessionStorage.removeItem("bma_cfg_v4");
  sessionStorage.removeItem("bma_cfg_ts");
  localStorage.removeItem("bma_cfg_v4");
  localStorage.removeItem("bma_cfg_ts");
} catch (e) {}

// Generator runtime signature internal (hanya berjalan di dalam memori closure modul)
async function getRuntimeProof(ts) {
  try {
    const windowNonce = Math.floor(ts / (30 * 1000));
    const seed = `bma_sec_runtime_v5_${windowNonce}`;
    const enc = new TextEncoder().encode(seed);
    const buf = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

let cachedConfig = null;

export async function fetchFirebaseConfig() {
  if (cachedConfig) return cachedConfig;
  const endpoints = [
    'https://xync.berrymodapk.com/config',
    'https://async.berrymodapk.com/config'
  ];

  try {
    const clientTs = Date.now();
    const clientProof = await getRuntimeProof(clientTs);
    let res = null;

    for (const ep of endpoints) {
      try {
        const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timeoutId = controller ? setTimeout(() => controller.abort(), 5000) : null;
        const r = await fetch(ep, {
          signal: controller ? controller.signal : undefined,
          headers: {
            "X-Berry-Client": "web-core",
            "X-Berry-Time": String(clientTs),
            "X-Berry-Signature": clientProof
          }
        });
        if (timeoutId) clearTimeout(timeoutId);
        if (r && r.ok) {
          res = r;
          break;
        }
      } catch (_) {}
    }

    if (!res || !res.ok) throw new Error("Akses tidak tersedia saat ini. Silakan coba kembali.");
    const rawData = await res.json().catch(() => ({}));
    
    let data;
    if (rawData && rawData._sec) {
      try {
        const sec = rawData._sec;
        if (typeof sec === "string" && sec.startsWith("v2.")) {
          const parts = sec.slice(3).split(".");
          const saltHex = parts[0];
          const rawBytes = atob(parts[1]);
          const seed = `bma_vault_${saltHex}_v2`;
          let chars = [];
          for (let i = 0; i < rawBytes.length; i++) {
            const k = (seed.charCodeAt(i % seed.length) ^ ((i * 17) & 0x7f)) & 0x7f;
            chars.push(String.fromCharCode(rawBytes.charCodeAt(i) ^ k));
          }
          let text = chars.join("");
          try { text = decodeURIComponent(text); } catch (_) {}
          data = JSON.parse(text);
        } else {
          let dec = atob(sec).split("").map((c) => String.fromCharCode(c.charCodeAt(0) ^ 0x3f)).join("");
          try { dec = decodeURIComponent(dec); } catch (_) {}
          data = JSON.parse(dec);
        }
      } catch (err) {
        console.error("Gagal mendekode konfigurasi worker:", err);
        data = rawData;
      }
    } else {
      data = rawData;
    }

    cachedConfig = data.mainConfig || data.firebaseConfig || data;
    if (!cachedConfig || !cachedConfig.apiKey) {
      throw new Error("Layanan sedang disiapkan. Silakan coba beberapa saat lagi.");
    }
    return cachedConfig;
  } catch (err) {
    throw err;
  }
}

let sdkPromise = null;

export async function initFirebaseSDK() {
  if (sdkPromise) return sdkPromise;

  sdkPromise = (async () => {
    try {
      const [
        firebaseConfig,
        { initializeApp, getApps },
        { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged },
        { getDatabase, ref, get, set, update, remove, push, onValue, query, orderByChild, limitToLast, equalTo, serverTimestamp }
      ] = await Promise.all([
        fetchFirebaseConfig(),
        import("https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js"),
        import("https://www.gstatic.com/firebasejs/10.7.1/firebase-auth.js"),
        import("https://www.gstatic.com/firebasejs/10.7.1/firebase-database.js")
      ]);

      const apps = getApps();
      const app = apps.length ? apps[0] : initializeApp(firebaseConfig);
      const auth = getAuth(app);
      const googleProvider = new GoogleAuthProvider();
      googleProvider.setCustomParameters({ prompt: "select_account" });
      const dbUrl = (firebaseConfig.databaseURL || "").replace(/\/$/, "");
      const rtdb = dbUrl ? getDatabase(app, dbUrl) : getDatabase(app);

      return {
        app,
        auth,
        googleProvider,
        signInWithPopup,
        signOut,
        onAuthStateChanged,
        rtdb,
        ref,
        get,
        set,
        update,
        remove,
        push,
        onValue,
        query,
        orderByChild,
        limitToLast,
        equalTo,
        serverTimestamp,
        databaseURL: dbUrl
      };
    } catch (err) {
      throw err;
    }
  })();

  return sdkPromise;
}

function getSafeRef(sdk, path) {
  if (!path || path === "/" || path === "") {
    return sdk.ref(sdk.rtdb);
  }
  return sdk.ref(sdk.rtdb, path.replace(/^\//, ""));
}

// REST fallback client for instant queries
class RTDBClient {
  async getBaseUrl() {
    const cfg = await fetchFirebaseConfig();
    return (cfg.databaseURL || "").replace(/\/$/, "");
  }

  async getIdToken() {
    try {
      const sdk = await initFirebaseSDK().catch(() => null);
      if (!sdk || !sdk.auth) return null;
      if (typeof sdk.auth.authStateReady === "function") {
        await sdk.auth.authStateReady().catch(() => null);
      }
      if (sdk.auth.currentUser) {
        return await sdk.auth.currentUser.getIdToken(false);
      }
    } catch (e) {
      // Ignore token fetch errors
    }
    return null;
  }

  async buildRestUrl(path) {
    const [baseUrl, token] = await Promise.all([
      this.getBaseUrl(),
      this.getIdToken()
    ]);
    const cleanPath = !path || path === "/" ? "" : path.replace(/^\//, "");
    const base = cleanPath ? `${baseUrl}/${cleanPath}.json` : `${baseUrl}/.json`;
    if (token) {
      const delimiter = base.includes("?") ? "&" : "?";
      return `${base}${delimiter}auth=${encodeURIComponent(token)}`;
    }
    return base;
  }

  async ping() {
    const t0 = Date.now();
    try {
      const baseUrl = await this.getBaseUrl().catch(() => null);
      if (baseUrl) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);
        await fetch(`${baseUrl}/system/maintenance.json?shallow=true`, {
          method: "GET",
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        const latency = Math.max(12, Date.now() - t0);
        return { online: true, latency };
      }
      return { online: navigator.onLine, latency: 28 };
    } catch (e) {
      const latency = Math.max(15, Date.now() - t0);
      return { online: navigator.onLine, latency: Math.min(latency, 250) };
    }
  }

  async get(path, timeoutMs = 2500) {
    try {
      const sdk = await initFirebaseSDK().catch(() => null);
      if (sdk) {
        const getPromise = sdk.get(getSafeRef(sdk, path)).then((snap) => (snap.exists() ? snap.val() : null));
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`SDK get timed out after ${timeoutMs}ms for ${path}`)), timeoutMs)
        );
        return await Promise.race([getPromise, timeoutPromise]);
      }
    } catch (sdkErr) {
      // SDK timed out or failed; silently use fast authenticated REST fallback
    }

    try {
      const url = await this.buildRestUrl(path);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000);
      const res = await fetch(url, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        console.warn(`[RTDB REST ${res.status}] for ${path}`);
        return null;
      }
      return await res.json();
    } catch (restErr) {
      console.warn(`[RTDB REST fallback failed for '${path}']:`, restErr?.message || restErr);
      return null;
    }
  }

  async set(path, data, timeoutMs = 4000) {
    try {
      const sdk = await initFirebaseSDK().catch(() => null);
      if (sdk) {
        const p = sdk.set(getSafeRef(sdk, path), data);
        const t = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), timeoutMs));
        return await Promise.race([p, t]);
      }
    } catch (e) {
      console.warn(`[RTDB set fallback for '${path}']`);
    }
    const url = await this.buildRestUrl(path);
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    });
    return await res.json();
  }

  async update(path, data, timeoutMs = 4000) {
    try {
      const sdk = await initFirebaseSDK().catch(() => null);
      if (sdk) {
        const p = sdk.update(getSafeRef(sdk, path), data);
        const t = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), timeoutMs));
        return await Promise.race([p, t]);
      }
    } catch (e) {
      console.warn(`[RTDB update fallback for '${path}']`);
    }
    const url = await this.buildRestUrl(path);
    const res = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    });
    return await res.json();
  }

  async remove(path, timeoutMs = 4000) {
    try {
      const sdk = await initFirebaseSDK().catch(() => null);
      if (sdk) {
        const p = sdk.remove(getSafeRef(sdk, path));
        const t = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), timeoutMs));
        return await Promise.race([p, t]);
      }
    } catch (e) {
      console.warn(`[RTDB remove fallback for '${path}']`);
    }
    const url = await this.buildRestUrl(path);
    const res = await fetch(url, {
      method: "DELETE"
    });
    return await res.json();
  }

  async push(path, data, timeoutMs = 4000) {
    try {
      const sdk = await initFirebaseSDK().catch(() => null);
      if (sdk) {
        const ref = sdk.push(getSafeRef(sdk, path));
        const p = sdk.set(ref, data).then(() => ref.key);
        const t = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), timeoutMs));
        return await Promise.race([p, t]);
      }
    } catch (e) {
      console.warn(`[RTDB push fallback for '${path}']`);
    }
    const url = await this.buildRestUrl(path);
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data)
    });
    const result = await res.json();
    return result?.name;
  }
}

export const rtdb = new RTDBClient();
